/** Reduce a free-text location to the US state the asset sits in.
 *
 *  Location arrives in whatever shape the source used — a metro name
 *  ("New York Metro"), a city/state pair ("Bronx, NY"), or a full street
 *  address ("1200 Distribution Pkwy, Cranbury, NJ 08512"). Three different
 *  ways of saying where a deal is makes the column unsortable and
 *  ungroupable, which is the whole reason to have it.
 *
 *  Resolution is deliberately conservative and ordered most-reliable first.
 *  When no state can be established the answer is null — the caller shows a
 *  dash and keeps the original text on hover. Guessing a state onto a deal
 *  would put a wrong jurisdiction on a credit file.
 */

export const US_STATES: Readonly<Record<string, string>> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  // The five inhabited territories the catalogue's State field also holds.
  PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', GU: 'Guam', AS: 'American Samoa', MP: 'Northern Mariana Islands',
}

/** The Canadian provinces and territories the catalogue's State / Province field also holds. */
export const CA_PROVINCES: Readonly<Record<string, string>> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
}

const CODES = new Set(Object.keys(US_STATES))

// Longest first so "West Virginia" is not consumed by "Virginia", and
// "New York" is not matched inside "New York Metro" *after* a shorter name.
const NAMES_BY_LENGTH: ReadonlyArray<readonly [string, string]> = Object.entries(US_STATES)
  .map(([code, name]) => [name.toLowerCase(), code] as const)
  .sort((a, b) => b[0].length - a[0].length)

/** Bare two-letter tokens that are far more often an English word than a
 *  state in this data — "OR", "IN", "ME" — and the territory codes that are
 *  words or numerals ("AS" in "as is", "VI" in "Phase VI"). Only honoured when
 *  the position makes them unambiguous (after a comma, or trailing before a
 *  ZIP). The server's twin is `AMBIGUOUS_STATE_CODES` in
 *  `backend/app/parsing/vocabularies.py`, held to this set by a test. */
const AMBIGUOUS = new Set(['OR', 'IN', 'ME', 'OK', 'HI', 'DE', 'LA', 'PA', 'MS', 'MT', 'MD', 'AS', 'VI'])

export type Geo = {
  /** Two-letter USPS code, e.g. "NJ". */
  code: string
  /** Full state name, for tooltips. */
  name: string
}

/**
 * @param location raw location text from the deal record
 * @returns the resolved state, or null when the text does not establish one
 */
export function geoFromLocation(location: string | null | undefined): Geo | null {
  const raw = (location ?? '').trim()
  if (!raw) return null

  const segments = raw.split(',').map((part) => part.trim()).filter(Boolean)

  // 1. "…, NJ 08512" or "…, NY" — a code in the final segments, which is where
  //    a postal address actually puts it. Most reliable, so it wins outright.
  for (const segment of segments.slice(1).reverse()) {
    const match = /^([A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?$/.exec(segment)
    const code = match?.[1]?.toUpperCase()
    if (code && CODES.has(code)) return { code, name: US_STATES[code] }
  }

  // 2. A trailing code with no comma before it: "Cranbury NJ 08512".
  const trailing = /\b([A-Za-z]{2})\s+\d{5}(?:-\d{4})?\s*$/.exec(raw)
  const trailingCode = trailing?.[1]?.toUpperCase()
  if (trailingCode && CODES.has(trailingCode)) {
    return { code: trailingCode, name: US_STATES[trailingCode] }
  }

  // 3. A full state name anywhere: "New York Metro", "Dallas, Texas".
  //    Longest-first so multi-word names win over their substrings.
  const lowered = raw.toLowerCase()
  for (const [name, code] of NAMES_BY_LENGTH) {
    // Word-bounded so "Indiana" cannot match inside "Indianapolis Ave".
    if (new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(lowered)) {
      return { code, name: US_STATES[code] }
    }
  }

  // 4. An unanchored bare code, e.g. "Bronx NY". Ambiguous tokens are refused
  //    here — "Portland OR" is a state, "1200 Distribution Pkwy" is not, and
  //    at this point there is nothing left to tell them apart.
  const tokens = raw.toUpperCase().match(/\b[A-Z]{2}\b/g) ?? []
  for (const token of tokens.reverse()) {
    if (CODES.has(token) && !AMBIGUOUS.has(token)) {
      return { code: token, name: US_STATES[token] }
    }
  }

  return null
}

/** Display form for a grid cell: the code, or a dash when unresolved. */
export function geoLabel(location: string | null | undefined): string {
  return geoFromLocation(location)?.code ?? '—'
}

// "…, Plano, TX 75093" / "Cranbury NJ 08512": whatever comes before a trailing
// state token, then an optional ZIP. The same expression the Pipeline Report's
// City column uses (`reporting/builder._city_state`), so a grid City and a
// report City never disagree about one location.
const CITY_STATE_RE = /^(.*?)[,\s]+([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/

/**
 * The city in a free-text location, or '' when the text names no state.
 *
 * Everything before the state is not the city: "4400 Willowbend Drive, Plano,
 * TX" has a street line in front of it, so the city is the last
 * comma-separated segment before the state. A location with no state token
 * ("New York Metro", a bare street) establishes no city — the column shows a
 * dash rather than print a street address or a metro name under City.
 */
export function cityFromLocation(location: string | null | undefined): string {
  const raw = (location ?? '').trim()
  if (!raw) return ''
  const match = CITY_STATE_RE.exec(raw)
  if (!match) return ''
  const head = match[1].trim().replace(/,+$/, '')
  const comma = head.lastIndexOf(',')
  return (comma >= 0 ? head.slice(comma + 1) : head).trim()
}
